import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../../components/ui/Button';

const DidYouKnowSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const navigate = useNavigate();

  const facts = [
    {
      emoji: '🏞️',
      title: 'There are nearly 2,500 state parks in the United States.',
      description: 'Every U.S. state has them—all 50 states—from tiny coastal preserves to massive wilderness parks.'
    },
    {
      emoji: '🌎',
      title: 'State park systems include more than 9,800 total recreation areas.',
      description: 'Beyond parks, states also manage beaches, historic sites, preserves, nature areas, and recreation areas.'
    },
    {
      emoji: '📏',
      title: 'State parks protect over 17–20 million acres of land.',
      description: 'That\'s an area larger than West Virginia—kept wild, protected, and open for adventure.'
    },
    {
      emoji: '🏕️',
      title: 'State parks offer more camping than the entire National Park System.',
      description: '217,000+ campsites nationwide\n\n100k+ car-accessible sites\n\n~95k primitive / hike-in / backcountry-style sites\n\nNational Parks? They have around 27,000 campsites total.'
    },
    {
      emoji: '🏆',
      title: 'The states with the most state parks:',
      description: 'New York – 164 parks\n\nFlorida – 158 parks\n\nWashington – 142 parks'
    },
    {
      emoji: '🚫',
      title: 'There are zero states without state parks.',
      description: 'Even the smallest states—Rhode Island, Delaware, and Hawaii—have dedicated state park systems.'
    },
    {
      emoji: '🏨',
      title: 'State parks also have hotels, lodges & cabins.',
      description: 'Across the country, you\'ll find 8,300+ cabins and lodge rooms, including full-service resort-style parks in states like Kentucky, Arkansas, Tennessee, and Texas.'
    },
    {
      emoji: '🌄',
      title: 'America\'s first state park was Niagara Falls State Park.',
      description: 'Founded in 1885, it protected the iconic natural wonder and launched the entire U.S. state-park movement.'
    }
  ];

  // Auto-rotation effect - rotates every 3 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % facts?.length);
    }, 3000);

    return () => clearInterval(interval);
  }, [facts?.length]);

  const currentFact = facts?.[currentIndex];

  const handlePlanTrip = () => {
    navigate('/ai-trip-planning-interface');
  };

  return (
    <section className="bg-white py-10 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-8">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2 flex items-center justify-center gap-2">
            <span className="text-3xl">🌲</span>
            Did You Know? – State Park Edition
          </h2>
        </div>

        {/* Carousel Container */}
        <div className="relative min-h-[280px] flex items-center justify-center">
          {/* Single Fact Card with Fade Animation */}
          <div 
            key={currentIndex}
            className="w-full bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-4 md:p-6 border border-green-100 shadow-lg animate-fadeIn"
          >
            <div className="flex flex-col md:flex-row md:items-start gap-3">
              {/* Emoji */}
              <div className="text-3xl md:text-4xl flex-shrink-0 self-center md:self-start">
                {currentFact?.emoji}
              </div>
              
              {/* Content */}
              <div className="flex-1">
                <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-2">
                  {currentFact?.title}
                </h3>
                <div className="text-sm md:text-base text-gray-700 leading-relaxed">
                  {currentFact?.description?.split('\n\n')?.map((paragraph, pIndex) => (
                    <p key={pIndex} className={pIndex > 0 ? 'mt-1.5' : ''}>
                      {paragraph}
                    </p>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Carousel Indicators */}
        <div className="flex justify-center gap-2 mt-6">
          {facts?.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                index === currentIndex 
                  ? 'bg-green-600 w-6' :'bg-gray-300 hover:bg-gray-400'
              }`}
              aria-label={`Go to fact ${index + 1}`}
            />
          ))}
        </div>

        {/* Call to Action */}
        <div className="text-center mt-8">
          <p className="text-base text-gray-600 mb-4">
            Ready to explore these amazing state parks? Let our AI help you plan the perfect adventure!
          </p>
          <Button 
            variant="default"
            size="lg"
            onClick={handlePlanTrip}
            iconName="ArrowRight"
            iconPosition="right"
            className="bg-green-800 hover:bg-green-900 text-white font-semibold shadow-lg hover:shadow-xl transition-all duration-200"
          >
            Start Planning Your Trip
          </Button>
        </div>
      </div>
    </section>
  );
};

export default DidYouKnowSection;